import { pluralRu } from "../format.ts";
import type { components } from "./schema";

type ErrorBody = components["schemas"]["ErrorBody"];

/** Ошибка HTTP API в конверте `{ error: { code, message, details, requestId } }`. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId: string | null;

  constructor(status: number, body: Partial<ErrorBody>, requestId: string | null) {
    super(body.message ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code ?? `http_${status}`;
    this.details = body.details ?? {};
    this.requestId = body.requestId ?? requestId;
  }

  /** Собирает ошибку из ответа openapi-fetch: тело может быть не конвертом (например, HTML от прокси). */
  static from(response: Response, body: unknown): ApiError {
    const envelope = isEnvelope(body) ? body.error : {};
    return new ApiError(response.status, envelope, response.headers.get("x-request-id"));
  }
}

function isEnvelope(body: unknown): body is { error: Partial<ErrorBody> } {
  return typeof body === "object" && body !== null && "error" in body && typeof body.error === "object";
}

const UNKNOWN_ERROR = "Что-то пошло не так";

// Логика опирается на стабильный error.code, а не на error.message backend'а.
const messages: Record<string, string> = {
  karaoke_assets_missing: "Песня ещё не подключена",
  karaoke_assets_invalid: "Файлы песни не прошли проверку",
  karaoke_storage_unavailable: "Не удалось загрузить песню. Попробуйте позже",
  device_update_required: "Нужно обновить прошивку открытки",
  palette_locked: "Эта палитра ещё не открыта: поднеси NFC-метку к E-va",
  network_error: "Нет связи с сервером. Проверьте интернет и попробуйте ещё раз",
  validation_error: "Проверьте введённые данные",
  rate_limited: "Слишком много попыток. Подождите минуту и попробуйте снова",
  rate_limit_unavailable: "Сервис временно недоступен. Попробуйте позже",
  dependency_unavailable: "Сервис временно недоступен. Попробуйте позже",
  not_authenticated: "Войдите, чтобы продолжить",
  invalid_access_token: "Сессия истекла. Войдите снова",
  invalid_refresh_token: "Сессия истекла. Войдите снова",
  refresh_token_reused: "Сессия истекла. Войдите снова",
  missing_refresh_token: "Войдите, чтобы продолжить",
  invalid_credentials: "Неверный email или пароль",
  email_already_registered: "Этот email уже зарегистрирован",
  invalid_telegram_login: "Не удалось войти через Telegram",
  telegram_account_conflict: "Этот Telegram уже привязан к другому аккаунту",
  device_not_found: "E-va не найдена",
  device_already_claimed: "Эта E-va уже привязана к другому аккаунту. Попросите владельца пригласить вас",
  invalid_device_claim_token: "QR-код устарел или уже использован. Отсканируйте новый QR-код на экране E-va",
  device_offline: "E-va не в сети",
  device_queue_full: "Очередь E-va переполнена. Попробуйте позже",
  message_not_found: "Сообщение не найдено",
  message_write_forbidden: "Нет доступа к этой E-va",
  // Прошлая попытка дошла до backend'а, хотя ответ потерялся, а затем текст или адресат изменились.
  idempotency_key_conflict: "Это сообщение уже отправлено. Проверьте ленту",
  invite_not_found: "Приглашение не найдено или отозвано. Попросите владельца E-va прислать новую ссылку",
  invite_expired: "Срок действия приглашения истёк. Попросите владельца E-va прислать новую ссылку",
  invite_usage_limit_reached: "Приглашение уже использовано. Попросите владельца E-va прислать новую ссылку",
  guest_blocked: "Владелец ограничил ваш доступ к этой E-va",
  guest_access_conflict: "Владелец ограничил ваш доступ к этой E-va",
  unsupported_media_type: "Этот формат файла не поддерживается",
  media_file_too_large: "Файл больше 5 МБ",
  media_not_processed: "Фото ещё обрабатывается. Попробуйте через минуту",
  media_asset_not_found: "Фото не найдено. Добавьте его заново",
};

/** Текст для пользователя; у неизвестной ошибки — общий текст и requestId для поддержки. */
export function errorMessage(error: unknown): string {
  const code = errorCode(error);
  const known = code ? messages[code] : undefined;
  if (known) return known;
  const requestId = error instanceof ApiError ? error.requestId : null;
  return requestId ? `${UNKNOWN_ERROR}. Код: ${requestId}` : UNKNOWN_ERROR;
}

function errorCode(error: unknown): string | null {
  if (error instanceof ApiError) return error.code;
  // fetch отклоняет промис с TypeError, если запрос не дошёл до сервера.
  if (error instanceof TypeError) return "network_error";
  return null;
}

type ValidationIssue = { loc?: unknown[]; type?: string; ctx?: Record<string, unknown> };

/**
 * Раскладывает `validation_error` по полям формы: поле — последний строковый элемент `loc`.
 * Проблемы с полями, которых в форме нет, не возвращаются: для них остаётся общий текст ошибки.
 */
export function fieldErrors<F extends string>(error: unknown, fields: readonly F[]): Partial<Record<F, string>> {
  const result: Partial<Record<F, string>> = {};
  if (!(error instanceof ApiError) || error.code !== "validation_error") return result;
  const issues = Array.isArray(error.details.errors) ? (error.details.errors as ValidationIssue[]) : [];
  for (const issue of issues) {
    const field = issue.loc?.findLast((part): part is string => typeof part === "string");
    if (!field || !fields.includes(field as F) || result[field as F]) continue;
    result[field as F] = issueMessage(field, issue);
  }
  return result;
}

function issueMessage(field: string, issue: ValidationIssue): string {
  const limit = Number(issue.ctx?.min_length ?? issue.ctx?.max_length);
  switch (issue.type) {
    case "missing":
      return "Заполните поле";
    case "string_too_short":
      return `Минимум ${limit} ${pluralChars(limit)}`;
    case "string_too_long":
      return `Не больше ${limit} ${pluralChars(limit)}`;
    default:
      return field === "email" ? "Некорректный email" : "Некорректное значение";
  }
}

function pluralChars(count: number): string {
  return pluralRu(count, ["символ", "символа", "символов"]);
}

/** Глобальный обработчик ошибок: пока пишет в console, позже сюда подключится мониторинг. */
export function reportError(error: unknown): void {
  if (error instanceof ApiError) {
    console.error(`[api] ${error.code} (status ${error.status}, requestId ${error.requestId ?? "—"})`, error);
  } else {
    console.error("[app]", error);
  }
}
